import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import AuthShell, { GoogleAuthButton } from "@/components/AuthShell";
import { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import api from "@/lib/api";

export default function Signup() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({
    name: "", email: "", password: "", confirm: "",
    specialty: "", fellowship_program: "",
  });
  const [specialties, setSpecialties] = useState([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/specialties").then(({ data }) => setSpecialties(data)).catch(() => {});
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (form.password !== form.confirm) {
      setError("Passwords do not match");
      return;
    }
    setLoading(true);
    try {
      await register({
        name: form.name,
        email: form.email,
        password: form.password,
        specialty: form.specialty || null,
        fellowship_program: form.fellowship_program || null,
      });
      toast.success("Account created — welcome!");
      nav("/dashboard");
    } catch (err) {
      setError(formatApiError(err, "Could not create account"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell
      title="Create your account"
      subtitle="Start logging cases in under a minute."
      footer={
        <>
          Already registered?{" "}
          <Link to="/signin" className="text-neu-accent font-semibold" data-testid="signup-to-signin">
            Sign in
          </Link>
        </>
      }
    >
      <form onSubmit={onSubmit} className="space-y-4" data-testid="signup-form">
        <div>
          <label className="text-sm font-medium block mb-2">Full name</label>
          <input
            required
            value={form.name}
            onChange={(e) => set("name", e.target.value)}
            className="neu-input"
            data-testid="signup-name"
          />
        </div>
        <div>
          <label className="text-sm font-medium block mb-2">Email</label>
          <input
            required
            type="email"
            value={form.email}
            onChange={(e) => set("email", e.target.value)}
            className="neu-input"
            data-testid="signup-email"
            autoComplete="email"
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-sm font-medium block mb-2">Password</label>
            <input
              required
              type="password"
              minLength={6}
              value={form.password}
              onChange={(e) => set("password", e.target.value)}
              className="neu-input"
              data-testid="signup-password"
              autoComplete="new-password"
            />
          </div>
          <div>
            <label className="text-sm font-medium block mb-2">Confirm</label>
            <input
              required
              type="password"
              minLength={6}
              value={form.confirm}
              onChange={(e) => set("confirm", e.target.value)}
              className="neu-input"
              data-testid="signup-confirm"
              autoComplete="new-password"
            />
          </div>
        </div>
        <div>
          <label className="text-sm font-medium block mb-2">Specialty</label>
          <select
            value={form.specialty}
            onChange={(e) => set("specialty", e.target.value)}
            className="neu-input"
            data-testid="signup-specialty"
          >
            <option value="">Select specialty…</option>
            {specialties.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="text-sm font-medium block mb-2">Fellowship program</label>
          <input
            value={form.fellowship_program}
            onChange={(e) => set("fellowship_program", e.target.value)}
            placeholder="e.g. HSS Orthopedic Surgery Fellowship"
            className="neu-input"
            data-testid="signup-program"
          />
        </div>

        {error && (
          <div className="text-sm text-red-600 bg-red-50 p-3 rounded-xl" data-testid="signup-error">
            {error}
          </div>
        )}
        <button type="submit" disabled={loading} className="btn-accent w-full" data-testid="signup-submit">
          {loading ? "Creating account…" : "Create account"}
        </button>

        <div className="relative py-2">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-neu-muted/30" />
          </div>
          <div className="relative flex justify-center text-xs">
            <span className="bg-[#E4EBF1] px-3 subtle">or</span>
          </div>
        </div>
        <GoogleAuthButton testId="signup-google" />
      </form>
    </AuthShell>
  );
}
